import { useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { Address } from 'components/pcc-components';
import { replaceTokensWithComponents } from '@cat-ecom/common/utils/stringUtils';
import { contactDealerCommonContent } from '../utils';
import { LocationAddress } from 'services/location/types';
import { getSeoUrl } from 'utils/links';
import { useEsiteCtx } from 'services/esite/context';
import { useParams } from 'next/navigation';
import cx from 'classnames';
import styles from './contact-dealer-popover-content.module.scss';

interface ContactDealerPopoverContentProps {
  dealerAddress: LocationAddress;
  locale: string;
  isLowerCaseLinkText?: boolean;
  isCustomerSupportEnabled?: boolean;
}

const ContactDealerPopoverContent = ({
  dealerAddress,
  locale,
  isLowerCaseLinkText = false,
  isCustomerSupportEnabled = false
}: ContactDealerPopoverContentProps) => {
  const { catalogId, langId, storeId } = useEsiteCtx();
  // TODO - Input Validation - Ensure that all values used from params are allowed values:
  // lang should always be a two-digit string without numbers or special characters (like 'es');
  // esite should always be a string without numbers or special characters, except for the dash (like demo-dealer).
  const params = useParams();
  const esiteUrl = getSeoUrl(params);

  const t = useTranslations();
  const textClassName = 'cat-u-theme-typography-footnote-bold mb-3';

  const {
    dealerPhone,
    dealerEmail,
    hasAddressInfo,
    addressObj,
    contactFormLink
  } = contactDealerCommonContent({
    dealerAddress,
    catalogId,
    langId,
    storeId,
    esiteUrl,
    isCustomerSupportEnabled
  });

  const emails = useMemo(() => dealerEmail?.split(','), [dealerEmail]);

  return (
    <div className={styles['contact-dealer-popover-content']}>
      {!!dealerPhone && (
        <div className={textClassName}>
          <span>{t('CONTACT_DEALER_CALL')}</span>
          <br />
          <a
            className={cx(
              styles['contact-dealer-popover-content__link'],
              'cat-u-theme-typography-footnote'
            )}
            href={`tel:${dealerPhone}`}
          >
            {dealerPhone}
          </a>
        </div>
      )}
      {!!dealerEmail && (
        <div className={textClassName}>
          <span>{t('EMAIL_US')}</span>
          <br />
          {emails?.length > 1 ? (
            emails.map((email) => (
              <a
                key={email}
                className={cx(
                  'd-block cat-u-theme-typography-footnote',
                  styles['contact-dealer-popover-content__link']
                )}
                href={`mailto:${email}`}
              >
                {email}
              </a>
            ))
          ) : (
            <a
              href={`mailto:${dealerEmail}`}
              className={cx(
                'cat-u-theme-typography-footnote',
                styles['contact-dealer-popover-content__link']
              )}
            >
              {dealerEmail}
            </a>
          )}
        </div>
      )}
      {hasAddressInfo && (
        <span className={textClassName}>
          <span>{t('CONTACT_DEALER_VISIT')}</span>
          <span className="cat-u-theme-typography-footnote">
            <Address locale={locale} address={addressObj} />
          </span>
        </span>
      )}
      <div className="mb-0 mt-4 cat-u-theme-typography-footnote">
        {replaceTokensWithComponents(
          t('CONTACT_DEALER_CONTACT_FORM', { '0': '{0}' }),
          <a
            className={styles['contact-dealer-popover-content__link']}
            href={contactFormLink}
            target="_blank"
          >
            {isLowerCaseLinkText
              ? t('CONTACT_FORM').toLowerCase()
              : t('CONTACT_FORM')}
          </a>
        )}
      </div>
    </div>
  );
};
export default ContactDealerPopoverContent;
